/**
 * API client for communicating with the Compendium backend.
 * 
 * Provides typed methods for:
 * - Authentication (getCurrentUser, logout)
 * - Essays (list, get, create, update, delete)
 * - Comments (list, create, update, delete, resolve) - Phase 2
 * - Suggestions (list, create, accept, reject) - Phase 2
 * - Workflow (status changes, versions, diffs, revert) - Phase 3
 * - Admin (users, stats) - Phase 3
 * 
 * All requests include credentials for session cookies.
 * 
 * Used by:
 * - src/hooks/useAuth.ts for authentication state
 * - src/pages/EssaysPage.tsx for essay list
 * - src/pages/EssayDetailPage.tsx for single essay
 * - src/components/CommentSidebar.tsx for comments
 * - src/components/InlineCommentPopover.tsx for inline comments
 * - src/components/SuggestionDisplay.tsx for suggestions
 * - src/components/StatusDropdown.tsx for status changes (Phase 3)
 * - src/components/VersionHistory.tsx for version history (Phase 3)
 * - src/pages/AdminPage.tsx for admin functions (Phase 3)
 */
import type {
  User,
  Essay,
  EssayListItem,
  EssayWithAuthor,
  ApiResult,
  CreateEssayRequest,
  UpdateEssayRequest,
  EssayStatus,
  CommentWithReplies,
  CommentWithUser,
  CreateCommentRequest,
  SuggestionWithUser,
  CreateSuggestionRequest,
  SuggestionStatus,
  StatusAction,
  EssayVersionWithUser,
  DiffResult,
  UserWithStats,
  AdminStats,
  UserRole,
  PublicUser,
  AudioGenerationResult,
  Notebook,
  Note,
  NoteWithLinks,
  CreateNotebookRequest,
  UpdateNotebookRequest,
  CreateNoteRequest,
  UpdateNoteRequest,
  NoteSearchResponse,
  SimilarItemsResponse,
  LinkedNoteFromEssay,
} from '@compendium/shared';

// =============================================================================
// Configuration
// =============================================================================

/**
 * Base URL for API requests.
 * In development, Vite proxy handles /api and /auth routes.
 * In production, nginx proxies these routes to the backend.
 */
const API_BASE = '';

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Make a fetch request with common options.
 * Includes credentials for session cookies and handles JSON responses.
 * 
 * @param url - API endpoint URL
 * @param options - Fetch options (method, body, etc.)
 * @returns Parsed JSON response
 * @throws Error if request fails or response is not JSON
 */
async function fetchApi<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    credentials: 'include', // Include cookies for session
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  // Parse JSON response
  const data = await response.json();
  return data as T;
}

// =============================================================================
// Authentication API
// =============================================================================

/**
 * Response type for /auth/me endpoint.
 */
interface AuthMeResponse {
  success: true;
  data: {
    user: User | null;
  };
}

/**
 * Get the currently authenticated user.
 * Returns null if not logged in.
 * 
 * Called by useAuth hook to check authentication state.
 */
export async function getCurrentUser(): Promise<User | null> {
  try {
    const response = await fetchApi<AuthMeResponse>('/auth/me');
    if (response.success) {
      return response.data.user;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Log out the current user by destroying their session.
 * Returns true if logout was successful.
 * 
 * Called by logout button in header.
 */
export async function logout(): Promise<boolean> {
  try {
    const response = await fetchApi<ApiResult<{ message: string }>>('/auth/logout', {
      method: 'POST',
    });
    return response.success;
  } catch {
    return false;
  }
}

/**
 * Get the Google OAuth login URL.
 * Redirecting to this URL will start the OAuth flow.
 */
export function getLoginUrl(): string {
  return `${API_BASE}/auth/google`;
}

// =============================================================================
// Essays API
// =============================================================================

/**
 * List response wrapper type.
 */
interface EssayListResponse {
  success: boolean;
  data?: EssayListItem[];
  error?: string;
}

/**
 * Single essay response wrapper type.
 */
interface EssayResponse {
  success: boolean;
  data?: EssayWithAuthor;
  error?: string;
}

/**
 * Fetch all essays, optionally filtered by status.
 * 
 * @param status - Optional status filter (draft, in_review, etc.)
 * @returns Array of essay list items (without full content)
 * 
 * Called by EssaysPage to display essay list.
 */
export async function getEssays(status?: EssayStatus): Promise<EssayListItem[]> {
  const url = status ? `/api/essays?status=${encodeURIComponent(status)}` : '/api/essays';
  const response = await fetchApi<EssayListResponse>(url);
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Failed to fetch essays');
}

/**
 * Fetch a single essay by slug.
 * Returns full essay content with author information.
 * 
 * @param slug - Essay URL slug
 * @returns Essay with full content and author
 * 
 * Called by EssayDetailPage to display essay.
 */
export async function getEssay(slug: string): Promise<EssayWithAuthor> {
  const response = await fetchApi<EssayResponse>(`/api/essays/${encodeURIComponent(slug)}`);
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Failed to fetch essay');
}

/**
 * Create a new essay.
 * Requires author role.
 * 
 * @param essay - Essay data (slug, title, content)
 * @returns Created essay
 */
export async function createEssay(essay: CreateEssayRequest): Promise<Essay> {
  const response = await fetchApi<ApiResult<Essay>>('/api/essays', {
    method: 'POST',
    body: JSON.stringify(essay),
  });
  
  if (response.success) {
    return response.data;
  }
  
  throw new Error((response as { error: string }).error || 'Failed to create essay');
}

/**
 * Update an existing essay.
 * Requires author role.
 * 
 * @param slug - Essay URL slug
 * @param updates - Fields to update
 * @returns Updated essay
 */
export async function updateEssay(slug: string, updates: UpdateEssayRequest): Promise<Essay> {
  const response = await fetchApi<ApiResult<Essay>>(`/api/essays/${encodeURIComponent(slug)}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
  
  if (response.success) {
    return response.data;
  }
  
  throw new Error((response as { error: string }).error || 'Failed to update essay');
}

/**
 * Delete an essay.
 * Requires author role.
 * 
 * @param slug - Essay URL slug
 */
export async function deleteEssay(slug: string): Promise<void> {
  const response = await fetchApi<ApiResult<{ message: string }>>(
    `/api/essays/${encodeURIComponent(slug)}`,
    { method: 'DELETE' }
  );
  
  if (!response.success) {
    throw new Error((response as { error: string }).error || 'Failed to delete essay');
  }
}

// =============================================================================
// Comments API (Phase 2)
// =============================================================================

/**
 * Response type for comments list endpoint.
 */
interface CommentsListResponse {
  success: boolean;
  data?: { comments: CommentWithReplies[] };
  error?: string;
}

/**
 * Response type for single comment operations.
 */
interface CommentResponse {
  success: boolean;
  data?: CommentWithUser;
  error?: string;
}

/**
 * Fetch all comments for an essay.
 * Returns nested structure with replies.
 * 
 * @param slug - Essay URL slug
 * @returns Array of top-level comments with nested replies
 * 
 * Called by CommentSidebar to display comments.
 */
export async function getComments(slug: string): Promise<CommentWithReplies[]> {
  const response = await fetchApi<CommentsListResponse>(
    `/api/essays/${encodeURIComponent(slug)}/comments`
  );
  
  if (response.success && response.data) {
    return response.data.comments;
  }
  
  throw new Error(response.error || 'Failed to fetch comments');
}

/**
 * Create a new comment on an essay.
 * 
 * @param slug - Essay URL slug
 * @param data - Comment data (type, content, position info)
 * @returns Created comment with user info
 * 
 * Called by InlineCommentPopover and CommentSidebar.
 */
export async function createComment(
  slug: string,
  data: CreateCommentRequest
): Promise<CommentWithUser> {
  const response = await fetchApi<CommentResponse>(
    `/api/essays/${encodeURIComponent(slug)}/comments`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Failed to create comment');
}

/**
 * Update a comment's content.
 * Only the comment owner can update.
 * 
 * @param id - Comment ID
 * @param content - New comment text
 * @returns Updated comment
 * 
 * Called by CommentSidebar edit functionality.
 */
export async function updateComment(id: number, content: string): Promise<CommentWithUser> {
  const response = await fetchApi<CommentResponse>(`/api/comments/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ content }),
  });
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Failed to update comment');
}

/**
 * Delete a comment.
 * Comment owner or essay author can delete.
 * 
 * @param id - Comment ID
 * 
 * Called by CommentSidebar delete functionality.
 */
export async function deleteComment(id: number): Promise<void> {
  const response = await fetchApi<ApiResult<{ message: string }>>(`/api/comments/${id}`, {
    method: 'DELETE',
  });
  
  if (!response.success) {
    throw new Error((response as { error: string }).error || 'Failed to delete comment');
  }
}

/**
 * Mark a comment thread as resolved.
 * Only essay author can resolve.
 * 
 * @param id - Comment ID
 * @returns Updated comment
 * 
 * Called by CommentSidebar resolve button.
 */
export async function resolveComment(id: number): Promise<CommentWithUser> {
  const response = await fetchApi<CommentResponse>(`/api/comments/${id}/resolve`, {
    method: 'POST',
  });
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Failed to resolve comment');
}

/**
 * Reopen a resolved comment thread.
 * Only essay author can unresolve.
 * 
 * @param id - Comment ID
 * @returns Updated comment
 * 
 * Called by CommentSidebar unresolve button.
 */
export async function unresolveComment(id: number): Promise<CommentWithUser> {
  const response = await fetchApi<CommentResponse>(`/api/comments/${id}/unresolve`, {
    method: 'POST',
  });
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Failed to unresolve comment');
}

// =============================================================================
// Suggestions API (Phase 2)
// =============================================================================

/**
 * Response type for suggestions list endpoint.
 */
interface SuggestionsListResponse {
  success: boolean;
  data?: { suggestions: SuggestionWithUser[] };
  error?: string;
}

/**
 * Response type for single suggestion operations.
 */
interface SuggestionResponse {
  success: boolean;
  data?: SuggestionWithUser;
  error?: string;
}

/**
 * Response type for accept suggestion (includes updated essay).
 */
interface AcceptSuggestionResponse {
  success: boolean;
  data?: { suggestion: SuggestionWithUser; essay: Essay };
  error?: string;
}

/**
 * Fetch all suggestions for an essay.
 * 
 * @param slug - Essay URL slug
 * @param status - Optional status filter (pending, accepted, rejected)
 * @returns Array of suggestions with user info
 * 
 * Called by EssayDetailPage to display suggestions.
 */
export async function getSuggestions(
  slug: string,
  status?: SuggestionStatus
): Promise<SuggestionWithUser[]> {
  const url = status
    ? `/api/essays/${encodeURIComponent(slug)}/suggestions?status=${encodeURIComponent(status)}`
    : `/api/essays/${encodeURIComponent(slug)}/suggestions`;
  
  const response = await fetchApi<SuggestionsListResponse>(url);
  
  if (response.success && response.data) {
    return response.data.suggestions;
  }
  
  throw new Error(response.error || 'Failed to fetch suggestions');
}

/**
 * Create a new text suggestion.
 * Requires editor or author role.
 * 
 * @param slug - Essay URL slug
 * @param data - Suggestion data (selection, original text, suggested text)
 * @returns Created suggestion with user info
 * 
 * Called by InlineCommentPopover suggest edit functionality.
 */
export async function createSuggestion(
  slug: string,
  data: CreateSuggestionRequest
): Promise<SuggestionWithUser> {
  const response = await fetchApi<SuggestionResponse>(
    `/api/essays/${encodeURIComponent(slug)}/suggestions`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Failed to create suggestion');
}

/**
 * Accept a suggestion and apply changes to essay.
 * Requires author role.
 * 
 * @param id - Suggestion ID
 * @returns Updated suggestion and essay
 * 
 * Called by SuggestionDisplay accept button.
 */
export async function acceptSuggestion(
  id: number
): Promise<{ suggestion: SuggestionWithUser; essay: Essay }> {
  const response = await fetchApi<AcceptSuggestionResponse>(`/api/suggestions/${id}/accept`, {
    method: 'PUT',
  });
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Failed to accept suggestion');
}

/**
 * Reject a suggestion without applying changes.
 * Requires author role.
 * 
 * @param id - Suggestion ID
 * @returns Updated suggestion
 * 
 * Called by SuggestionDisplay reject button.
 */
export async function rejectSuggestion(id: number): Promise<SuggestionWithUser> {
  const response = await fetchApi<SuggestionResponse>(`/api/suggestions/${id}/reject`, {
    method: 'PUT',
  });
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Failed to reject suggestion');
}

// =============================================================================
// Workflow API (Phase 3)
// =============================================================================

/**
 * Response type for status change endpoint.
 */
interface StatusChangeResponse {
  success: boolean;
  data?: {
    essay: {
      id: number;
      slug: string;
      title: string;
      status: EssayStatus;
      updatedAt: string;
      author: PublicUser;
    };
    version: {
      id: number;
      versionNumber: number;
      status: EssayStatus;
      changeSummary: string | null;
    };
  };
  error?: string;
}

/**
 * Response type for versions list endpoint.
 */
interface VersionsListResponse {
  success: boolean;
  data?: { versions: EssayVersionWithUser[] };
  error?: string;
}

/**
 * Response type for single version endpoint.
 */
interface VersionResponse {
  success: boolean;
  data?: { version: EssayVersionWithUser };
  error?: string;
}

/**
 * Response type for diff endpoint.
 */
interface DiffResponse {
  success: boolean;
  data?: DiffResult;
  error?: string;
}

/**
 * Response type for revert endpoint.
 */
interface RevertResponse {
  success: boolean;
  data?: {
    essay: Essay;
    version: {
      id: number;
      versionNumber: number;
      changeSummary: string | null;
    };
    revertedTo: number;
  };
  error?: string;
}

/**
 * Change an essay's status using the workflow state machine.
 * Creates a new version documenting the status change.
 * Requires author role.
 * 
 * @param slug - Essay URL slug
 * @param action - The transition action to perform
 * @param changeSummary - Optional description of the change
 * @returns Updated essay info and new version
 * 
 * Called by StatusDropdown component.
 */
export async function changeEssayStatus(
  slug: string,
  action: StatusAction,
  changeSummary?: string
): Promise<StatusChangeResponse['data']> {
  const response = await fetchApi<StatusChangeResponse>(
    `/api/essays/${encodeURIComponent(slug)}/status`,
    {
      method: 'PUT',
      body: JSON.stringify({ action, changeSummary }),
    }
  );
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Failed to change essay status');
}

/**
 * Get version history for an essay.
 * Returns all versions ordered by version number (newest first).
 * 
 * @param slug - Essay URL slug
 * @returns Array of versions with user info
 * 
 * Called by VersionHistory component.
 */
export async function getVersions(slug: string): Promise<EssayVersionWithUser[]> {
  const response = await fetchApi<VersionsListResponse>(
    `/api/essays/${encodeURIComponent(slug)}/versions`
  );
  
  if (response.success && response.data) {
    return response.data.versions;
  }
  
  throw new Error(response.error || 'Failed to fetch versions');
}

/**
 * Get a specific version of an essay.
 * Returns full content and metadata.
 * 
 * @param slug - Essay URL slug
 * @param versionId - Version ID
 * @returns Version with user info
 * 
 * Called by VersionHistory component (view version).
 */
export async function getVersion(
  slug: string,
  versionId: number
): Promise<EssayVersionWithUser> {
  const response = await fetchApi<VersionResponse>(
    `/api/essays/${encodeURIComponent(slug)}/versions/${versionId}`
  );
  
  if (response.success && response.data) {
    return response.data.version;
  }
  
  throw new Error(response.error || 'Failed to fetch version');
}

/**
 * Get diff between a version and its previous version.
 * Returns diff segments and statistics.
 * 
 * @param slug - Essay URL slug
 * @param versionId - Version ID to compare
 * @returns Diff result with segments and stats
 * 
 * Called by DiffViewer component.
 */
export async function getVersionDiff(
  slug: string,
  versionId: number
): Promise<DiffResult> {
  const response = await fetchApi<DiffResponse>(
    `/api/essays/${encodeURIComponent(slug)}/versions/${versionId}/diff`
  );
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Failed to fetch diff');
}

/**
 * Revert an essay to a previous version.
 * Creates a new version with the old content.
 * Requires author role.
 * 
 * @param slug - Essay URL slug
 * @param versionId - Version ID to revert to
 * @returns Updated essay and new version info
 * 
 * Called by VersionHistory component (revert button).
 */
export async function revertToVersion(
  slug: string,
  versionId: number
): Promise<RevertResponse['data']> {
  const response = await fetchApi<RevertResponse>(
    `/api/essays/${encodeURIComponent(slug)}/revert/${versionId}`,
    { method: 'POST' }
  );
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Failed to revert to version');
}

// =============================================================================
// Admin API (Phase 3)
// =============================================================================

/**
 * Response type for users list endpoint.
 */
interface UsersListResponse {
  success: boolean;
  data?: {
    users: UserWithStats[];
    total: number;
    pending: number;
  };
  error?: string;
}

/**
 * Response type for single user operations.
 */
interface UserResponse {
  success: boolean;
  data?: { user: UserWithStats };
  error?: string;
}

/**
 * Response type for admin stats endpoint.
 */
interface AdminStatsResponse {
  success: boolean;
  data?: AdminStats;
  error?: string;
}

/**
 * Get all users with activity statistics.
 * Supports filtering by status and role.
 * Requires author role.
 * 
 * @param status - Filter by status ('pending', 'approved', 'blocked', 'all')
 * @param role - Filter by user role
 * @returns Users with stats and counts
 *
 * Called by UserManagement component.
 */
export async function getUsers(
  status?: 'pending' | 'approved' | 'blocked' | 'all',
  role?: UserRole
): Promise<{ users: UserWithStats[]; total: number; pending: number }> {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (role) params.set('role', role);
  
  const url = params.toString()
    ? `/api/admin/users?${params.toString()}`
    : '/api/admin/users';
  
  const response = await fetchApi<UsersListResponse>(url);
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Failed to fetch users');
}

/**
 * Get users pending approval.
 * Convenience function for approval queue.
 * Requires author role.
 * 
 * @returns Pending users with stats
 * 
 * Called by AdminPage pending tab.
 */
export async function getPendingUsers(): Promise<UserWithStats[]> {
  const response = await fetchApi<UsersListResponse>('/api/admin/users/pending');
  
  if (response.success && response.data) {
    return response.data.users;
  }
  
  throw new Error(response.error || 'Failed to fetch pending users');
}

/**
 * Approve a pending user.
 * Requires author role.
 * 
 * @param userId - User ID to approve
 * @returns Updated user
 * 
 * Called by UserManagement approve button.
 */
export async function approveUser(userId: number): Promise<UserWithStats> {
  const response = await fetchApi<UserResponse>(
    `/api/admin/users/${userId}/approve`,
    { method: 'PUT' }
  );
  
  if (response.success && response.data) {
    return response.data.user;
  }
  
  throw new Error(response.error || 'Failed to approve user');
}

/**
 * Reject a pending user (deletes their account).
 * Requires author role.
 * 
 * @param userId - User ID to reject
 * 
 * Called by UserManagement reject button.
 */
export async function rejectUser(userId: number): Promise<void> {
  const response = await fetchApi<ApiResult<{ message: string }>>(
    `/api/admin/users/${userId}/reject`,
    { method: 'PUT' }
  );
  
  if (!response.success) {
    throw new Error((response as { error: string }).error || 'Failed to reject user');
  }
}

/**
 * Change a user's role.
 * Requires author role.
 * 
 * @param userId - User ID
 * @param role - New role ('editor' or 'reviewer')
 * @returns Updated user
 * 
 * Called by UserManagement role dropdown.
 */
export async function changeUserRole(
  userId: number,
  role: UserRole
): Promise<UserWithStats> {
  const response = await fetchApi<UserResponse>(
    `/api/admin/users/${userId}/role`,
    {
      method: 'PUT',
      body: JSON.stringify({ role }),
    }
  );
  
  if (response.success && response.data) {
    return response.data.user;
  }
  
  throw new Error(response.error || 'Failed to change user role');
}

/**
 * Block a user from commenting.
 * Requires author role.
 *
 * @param userId - User ID to block
 * @returns Updated user
 *
 * Called by UserManagement block button.
 */
export async function blockUser(userId: number): Promise<UserWithStats> {
  const response = await fetchApi<UserResponse>(
    `/api/admin/users/${userId}/block`,
    { method: 'PUT' }
  );

  if (response.success && response.data) {
    return response.data.user;
  }

  throw new Error(response.error || 'Failed to block user');
}

/**
 * Unblock a blocked user.
 * Requires author role.
 *
 * @param userId - User ID to unblock
 * @returns Updated user
 *
 * Called by UserManagement unblock button.
 */
export async function unblockUser(userId: number): Promise<UserWithStats> {
  const response = await fetchApi<UserResponse>(
    `/api/admin/users/${userId}/unblock`,
    { method: 'PUT' }
  );

  if (response.success && response.data) {
    return response.data.user;
  }

  throw new Error(response.error || 'Failed to unblock user');
}

/**
 * Get system-wide statistics for admin dashboard.
 * Requires author role.
 * 
 * @returns Admin statistics
 * 
 * Called by DashboardStats component.
 */
export async function getAdminStats(): Promise<AdminStats> {
  const response = await fetchApi<AdminStatsResponse>('/api/admin/stats');
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Failed to fetch admin stats');
}

// =============================================================================
// Comment Moderation API
// =============================================================================

export interface FlaggedComment {
  id: number;
  content: string;
  flagReason: string | null;
  createdAt: string;
  user: { id: number; name: string; email: string; avatarUrl: string | null } | null;
  essay: { slug: string; title: string } | null;
}

/**
 * Get all flagged comments.
 * Requires author role.
 *
 * Called by ModerationTab component.
 */
export async function getFlaggedComments(): Promise<FlaggedComment[]> {
  const response = await fetchApi<ApiResult<{ comments: FlaggedComment[]; total: number }>>(
    '/api/admin/comments/flagged'
  );

  if (response.success && response.data) {
    return response.data.comments;
  }

  throw new Error((response as { error: string }).error || 'Failed to fetch flagged comments');
}

/**
 * Unflag a comment (approve it).
 * Requires author role.
 */
export async function unflagComment(commentId: number): Promise<void> {
  const response = await fetchApi<ApiResult<{ message: string }>>(
    `/api/admin/comments/${commentId}/unflag`,
    { method: 'PUT' }
  );

  if (!response.success) {
    throw new Error((response as { error: string }).error || 'Failed to unflag comment');
  }
}

/**
 * Delete a flagged comment.
 * Requires author role.
 */
export async function deleteFlaggedComment(commentId: number): Promise<void> {
  const response = await fetchApi<ApiResult<{ message: string }>>(
    `/api/admin/comments/${commentId}`,
    { method: 'DELETE' }
  );

  if (!response.success) {
    throw new Error((response as { error: string }).error || 'Failed to delete comment');
  }
}

// =============================================================================
// Analytics API
// =============================================================================

interface AnalyticsOverview {
  totalViews: number;
  uniqueVisitors: number;
  avgDuration: number;
  from: string;
  to: string;
}

interface AnalyticsPage {
  path: string;
  essaySlug: string | null;
  views: number;
  uniqueVisitors: number;
  avgDuration: number;
}

interface AnalyticsReferrer {
  referrer: string;
  views: number;
  uniqueVisitors: number;
}

interface AnalyticsTimeseries {
  date: string;
  views: number;
  uniqueVisitors: number;
}

interface AnalyticsEssay {
  essaySlug: string;
  views: number;
  uniqueVisitors: number;
  avgDuration: number;
}

interface AnalyticsDevices {
  devices: { name: string; views: number }[];
  browsers: { name: string; views: number }[];
}

interface AnalyticsRecentView {
  id: number;
  path: string;
  essaySlug: string | null;
  visitorHash: string;
  userAgent: string | null;
  referrer: string | null;
  duration: number | null;
  country: string | null;
  viewedAt: string;
}

export type { AnalyticsOverview, AnalyticsPage, AnalyticsReferrer, AnalyticsTimeseries, AnalyticsEssay, AnalyticsDevices, AnalyticsRecentView };

function analyticsParams(from?: string, to?: string): string {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function getAnalyticsOverview(from?: string, to?: string): Promise<AnalyticsOverview> {
  const response = await fetchApi<{ success: boolean; data?: AnalyticsOverview; error?: string }>(`/api/admin/analytics/overview${analyticsParams(from, to)}`);
  if (response.success && response.data) return response.data;
  throw new Error(response.error || 'Failed to fetch analytics overview');
}

export async function getAnalyticsPages(from?: string, to?: string): Promise<AnalyticsPage[]> {
  const response = await fetchApi<{ success: boolean; data?: AnalyticsPage[]; error?: string }>(`/api/admin/analytics/pages${analyticsParams(from, to)}`);
  if (response.success && response.data) return response.data;
  throw new Error(response.error || 'Failed to fetch page analytics');
}

export async function getAnalyticsReferrers(from?: string, to?: string): Promise<AnalyticsReferrer[]> {
  const response = await fetchApi<{ success: boolean; data?: AnalyticsReferrer[]; error?: string }>(`/api/admin/analytics/referrers${analyticsParams(from, to)}`);
  if (response.success && response.data) return response.data;
  throw new Error(response.error || 'Failed to fetch referrer analytics');
}

export async function getAnalyticsTimeseries(from?: string, to?: string): Promise<AnalyticsTimeseries[]> {
  const response = await fetchApi<{ success: boolean; data?: AnalyticsTimeseries[]; error?: string }>(`/api/admin/analytics/timeseries${analyticsParams(from, to)}`);
  if (response.success && response.data) return response.data;
  throw new Error(response.error || 'Failed to fetch timeseries');
}

export async function getAnalyticsEssays(from?: string, to?: string): Promise<AnalyticsEssay[]> {
  const response = await fetchApi<{ success: boolean; data?: AnalyticsEssay[]; error?: string }>(`/api/admin/analytics/essays${analyticsParams(from, to)}`);
  if (response.success && response.data) return response.data;
  throw new Error(response.error || 'Failed to fetch essay analytics');
}

export async function getAnalyticsDevices(from?: string, to?: string): Promise<AnalyticsDevices> {
  const response = await fetchApi<{ success: boolean; data?: AnalyticsDevices; error?: string }>(`/api/admin/analytics/devices${analyticsParams(from, to)}`);
  if (response.success && response.data) return response.data;
  throw new Error(response.error || 'Failed to fetch device analytics');
}

export async function getAnalyticsRecent(limit?: number): Promise<AnalyticsRecentView[]> {
  const params = limit ? `?limit=${limit}` : '';
  const response = await fetchApi<{ success: boolean; data?: AnalyticsRecentView[]; error?: string }>(`/api/admin/analytics/recent${params}`);
  if (response.success && response.data) return response.data;
  throw new Error(response.error || 'Failed to fetch recent views');
}

export interface VisitorPageData {
  path: string;
  essaySlug: string | null;
  views: number;
  totalDuration: number;
  avgDuration: number;
  videoProgress: number | null;
  scrollDepth: number | null;
  firstSeen: string;
  lastSeen: string;
}

export interface VisitorJourney {
  visitorHash: string;
  label: string | null;
  ipAddress: string | null;
  country: string | null;
  city: string | null;
  device: string;
  browser: string;
  firstSeen: string;
  lastSeen: string;
  totalViews: number;
  totalDuration: number;
  pages: VisitorPageData[];
}

export interface VisitorLabel {
  visitorHash: string;
  label: string;
  createdAt: string;
  updatedAt: string;
}

export interface VisitorAnalyticsResponse {
  visitors: VisitorJourney[];
  essayOrder: string[];
}

export async function getAnalyticsVisitors(from?: string, to?: string): Promise<VisitorAnalyticsResponse> {
  const response = await fetchApi<{ success: boolean; data?: VisitorJourney[]; essayOrder?: string[]; error?: string }>(`/api/admin/analytics/visitors${analyticsParams(from, to)}`);
  if (response.success && response.data) return { visitors: response.data, essayOrder: response.essayOrder || [] };
  throw new Error(response.error || 'Failed to fetch visitor analytics');
}

export async function setVisitorLabel(visitorHash: string, label: string): Promise<void> {
  const response = await fetchApi<{ success: boolean; error?: string }>(`/api/admin/analytics/labels/${encodeURIComponent(visitorHash)}`, {
    method: 'PUT',
    body: JSON.stringify({ label }),
  });
  if (!response.success) throw new Error(response.error || 'Failed to update label');
}

export interface VisitorDetailEvent {
  id: number;
  path: string;
  essaySlug: string | null;
  duration: number | null;
  videoProgress: number | null;
  scrollDepth: number | null;
  referrer: string | null;
  viewedAt: string;
}

export interface VisitorDetailData {
  visitor: {
    visitorHash: string;
    label: string | null;
    ipAddress: string | null;
    country: string | null;
    city: string | null;
    device: string;
    browser: string;
  };
  stats: {
    totalViews: number;
    uniquePages: number;
    totalDuration: number;
    firstSeen: string;
    lastSeen: string;
  };
  events: VisitorDetailEvent[];
}

export async function getVisitorDetail(visitorHash: string): Promise<VisitorDetailData> {
  const response = await fetchApi<{ success: boolean; data?: VisitorDetailData; error?: string }>(
    `/api/admin/analytics/visitors/${encodeURIComponent(visitorHash)}`
  );
  if (response.success && response.data) return response.data;
  throw new Error(response.error || 'Failed to fetch visitor detail');
}

// =============================================================================
// Attachments API (Phase 4)
// =============================================================================

import type {
  Attachment,
  Category,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  Tag,
  CreateTagRequest,
  Series,
  SeriesWithEssays,
  EssaySeriesInfo,
  CreateSeriesRequest,
  UpdateSeriesRequest,
  SearchResponse,
} from '@compendium/shared';

/**
 * Response type for attachments list endpoint.
 */
interface AttachmentsListResponse {
  success: boolean;
  data?: { attachments: Attachment[] };
  error?: string;
}

/**
 * Response type for single attachment operations.
 */
interface AttachmentResponse {
  success: boolean;
  data?: Attachment;
  error?: string;
}

/**
 * Get all attachments for an essay.
 * 
 * @param slug - Essay URL slug
 * @returns Array of attachments
 * 
 * Called by AttachmentsPanel component.
 */
export async function getAttachments(slug: string): Promise<Attachment[]> {
  const response = await fetchApi<AttachmentsListResponse>(
    `/api/essays/${encodeURIComponent(slug)}/attachments`
  );
  
  if (response.success && response.data) {
    return response.data.attachments;
  }
  
  throw new Error(response.error || 'Failed to fetch attachments');
}

/**
 * Upload files to an essay.
 * Uses FormData for multipart file upload.
 * 
 * @param slug - Essay URL slug
 * @param files - Array of files to upload
 * @returns Array of created attachments
 * 
 * Called by AttachmentsPanel upload functionality.
 */
export async function uploadAttachments(
  slug: string,
  files: File[]
): Promise<Attachment[]> {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });

  const response = await fetch(
    `/api/essays/${encodeURIComponent(slug)}/attachments`,
    {
      method: 'POST',
      credentials: 'include',
      body: formData,
    }
  );

  const data = await response.json();
  
  if (data.success && data.data) {
    return data.data.attachments;
  }
  
  throw new Error(data.error || 'Failed to upload attachments');
}

/**
 * Delete an attachment.
 * 
 * @param id - Attachment ID
 * 
 * Called by AttachmentsPanel delete button.
 */
export async function deleteAttachment(id: number): Promise<void> {
  const response = await fetchApi<ApiResult<{ message: string }>>(
    `/api/attachments/${id}`,
    { method: 'DELETE' }
  );
  
  if (!response.success) {
    throw new Error((response as { error: string }).error || 'Failed to delete attachment');
  }
}

/**
 * Toggle attachment visibility.
 * 
 * @param id - Attachment ID
 * @param isPublic - New visibility setting
 * @returns Updated attachment
 * 
 * Called by AttachmentsPanel visibility toggle.
 */
export async function setAttachmentVisibility(
  id: number,
  isPublic: boolean
): Promise<Attachment> {
  const response = await fetchApi<AttachmentResponse>(
    `/api/attachments/${id}/visibility`,
    {
      method: 'PUT',
      body: JSON.stringify({ isPublic }),
    }
  );
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Failed to update attachment visibility');
}

/**
 * Get attachment download URL.
 */
export function getAttachmentUrl(id: number): string {
  return `/api/attachments/${id}`;
}

// =============================================================================
// Linked Notes API (Essay → Note)
// =============================================================================

/**
 * Get standalone notes linked to an essay.
 *
 * @param slug - Essay URL slug
 * @returns Array of linked notes with similarity info
 *
 * Called by EssayDetailPage LinkedNotesPanel.
 */
export async function getLinkedNotes(slug: string): Promise<LinkedNoteFromEssay[]> {
  const response = await fetchApi<ApiResult<{ notes: LinkedNoteFromEssay[] }>>(
    `/api/essays/${encodeURIComponent(slug)}/linked-notes`
  );

  if (response.success && response.data) {
    return response.data.notes;
  }

  throw new Error((response as { error?: string }).error || 'Failed to fetch linked notes');
}

/**
 * Manually link a note to an essay.
 *
 * @param slug - Essay URL slug
 * @param noteId - Note ID to link
 */
export async function linkNoteToEssay(slug: string, noteId: number): Promise<void> {
  const response = await fetchApi<ApiResult<{ message: string }>>(
    `/api/essays/${encodeURIComponent(slug)}/linked-notes`,
    {
      method: 'POST',
      body: JSON.stringify({ noteId }),
    }
  );

  if (!response.success) {
    throw new Error((response as { error?: string }).error || 'Failed to link note');
  }
}

/**
 * Remove a manual link between a note and an essay.
 *
 * @param slug - Essay URL slug
 * @param noteId - Note ID to unlink
 */
export async function unlinkNoteFromEssay(slug: string, noteId: number): Promise<void> {
  const response = await fetchApi<ApiResult<{ message: string }>>(
    `/api/essays/${encodeURIComponent(slug)}/linked-notes/${noteId}`,
    { method: 'DELETE' }
  );

  if (!response.success) {
    throw new Error((response as { error?: string }).error || 'Failed to unlink note');
  }
}

// =============================================================================
// Categories API (Phase 4)
// =============================================================================

/**
 * Response type for categories list endpoint.
 */
interface CategoriesListResponse {
  success: boolean;
  data?: { categories: Category[] };
  error?: string;
}

/**
 * Response type for single category operations.
 */
interface CategoryResponse {
  success: boolean;
  data?: Category;
  error?: string;
}

/**
 * Get all categories.
 * 
 * @returns Array of categories
 * 
 * Called by CategoryTagSelector component.
 */
export async function getCategories(): Promise<Category[]> {
  const response = await fetchApi<CategoriesListResponse>('/api/categories');
  
  if (response.success && response.data) {
    return response.data.categories;
  }
  
  throw new Error(response.error || 'Failed to fetch categories');
}

/**
 * Create a category.
 * 
 * @param data - Category data
 * @returns Created category
 */
export async function createCategory(data: CreateCategoryRequest): Promise<Category> {
  const response = await fetchApi<CategoryResponse>(
    '/api/categories',
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Failed to create category');
}

/**
 * Update a category.
 * 
 * @param id - Category ID
 * @param data - Updated category data
 * @returns Updated category
 */
export async function updateCategory(
  id: number,
  data: UpdateCategoryRequest
): Promise<Category> {
  const response = await fetchApi<CategoryResponse>(
    `/api/categories/${id}`,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    }
  );
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Failed to update category');
}

/**
 * Delete a category.
 * 
 * @param id - Category ID
 */
export async function deleteCategory(id: number): Promise<void> {
  const response = await fetchApi<ApiResult<{ message: string }>>(
    `/api/categories/${id}`,
    { method: 'DELETE' }
  );
  
  if (!response.success) {
    throw new Error((response as { error: string }).error || 'Failed to delete category');
  }
}

/**
 * Set essay category.
 * 
 * @param slug - Essay URL slug
 * @param categoryId - Category ID (or null to remove)
 * @returns Updated category info
 */
export async function setEssayCategory(
  slug: string,
  categoryId: number | null
): Promise<Category | null> {
  const response = await fetchApi<{ success: boolean; data?: { category: Category | null }; error?: string }>(
    `/api/essays/${encodeURIComponent(slug)}/category`,
    {
      method: 'PUT',
      body: JSON.stringify({ categoryId }),
    }
  );
  
  if (response.success && response.data) {
    return response.data.category;
  }
  
  throw new Error(response.error || 'Failed to set essay category');
}

// =============================================================================
// Tags API (Phase 4)
// =============================================================================

/**
 * Response type for tags list endpoint.
 */
interface TagsListResponse {
  success: boolean;
  data?: { tags: Tag[] };
  error?: string;
}

/**
 * Response type for single tag operations.
 */
interface TagResponse {
  success: boolean;
  data?: Tag;
  error?: string;
}

/**
 * Get all tags.
 * 
 * @returns Array of tags
 * 
 * Called by CategoryTagSelector component.
 */
export async function getTags(): Promise<Tag[]> {
  const response = await fetchApi<TagsListResponse>('/api/tags');
  
  if (response.success && response.data) {
    return response.data.tags;
  }
  
  throw new Error(response.error || 'Failed to fetch tags');
}

/**
 * Create a tag.
 * 
 * @param data - Tag data
 * @returns Created tag
 */
export async function createTag(data: CreateTagRequest): Promise<Tag> {
  const response = await fetchApi<TagResponse>(
    '/api/tags',
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Failed to create tag');
}

/**
 * Delete a tag.
 * 
 * @param id - Tag ID
 */
export async function deleteTag(id: number): Promise<void> {
  const response = await fetchApi<ApiResult<{ message: string }>>(
    `/api/tags/${id}`,
    { method: 'DELETE' }
  );
  
  if (!response.success) {
    throw new Error((response as { error: string }).error || 'Failed to delete tag');
  }
}

/**
 * Get tags for an essay.
 * 
 * @param slug - Essay URL slug
 * @returns Array of tags
 */
export async function getEssayTags(slug: string): Promise<Tag[]> {
  const response = await fetchApi<TagsListResponse>(
    `/api/essays/${encodeURIComponent(slug)}/tags`
  );
  
  if (response.success && response.data) {
    return response.data.tags;
  }
  
  throw new Error(response.error || 'Failed to fetch essay tags');
}

/**
 * Set tags for an essay.
 * 
 * @param slug - Essay URL slug
 * @param tagIds - Array of tag IDs
 * @returns Updated tags
 */
export async function setEssayTags(
  slug: string,
  tagIds: number[]
): Promise<Tag[]> {
  const response = await fetchApi<TagsListResponse>(
    `/api/essays/${encodeURIComponent(slug)}/tags`,
    {
      method: 'PUT',
      body: JSON.stringify({ tagIds }),
    }
  );
  
  if (response.success && response.data) {
    return response.data.tags;
  }
  
  throw new Error(response.error || 'Failed to set essay tags');
}

// =============================================================================
// Series API (Phase 4)
// =============================================================================

/**
 * Response type for series list endpoint.
 */
interface SeriesListResponse {
  success: boolean;
  data?: { series: Series[] };
  error?: string;
}

/**
 * Response type for single series with essays.
 */
interface SeriesWithEssaysResponse {
  success: boolean;
  data?: SeriesWithEssays;
  error?: string;
}

/**
 * Response type for single series operations.
 */
interface SeriesResponse {
  success: boolean;
  data?: Series;
  error?: string;
}

/**
 * Response type for essay series info.
 */
interface EssaySeriesResponse {
  success: boolean;
  data?: { seriesInfo: EssaySeriesInfo[] };
  error?: string;
}

/**
 * Get all series.
 * 
 * @returns Array of series
 * 
 * Called by SeriesManager component.
 */
export async function getAllSeries(): Promise<Series[]> {
  const response = await fetchApi<SeriesListResponse>('/api/series');
  
  if (response.success && response.data) {
    return response.data.series;
  }
  
  throw new Error(response.error || 'Failed to fetch series');
}

/**
 * Get a series with its essays.
 * 
 * @param slug - Series URL slug
 * @returns Series with ordered essays
 * 
 * Called by SeriesPage component.
 */
export async function getSeriesWithEssays(slug: string): Promise<SeriesWithEssays> {
  const response = await fetchApi<SeriesWithEssaysResponse>(
    `/api/series/${encodeURIComponent(slug)}`
  );
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Failed to fetch series');
}

/**
 * Create a series.
 * 
 * @param data - Series data
 * @returns Created series
 */
export async function createSeries(data: CreateSeriesRequest): Promise<Series> {
  const response = await fetchApi<SeriesResponse>(
    '/api/series',
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Failed to create series');
}

/**
 * Update a series.
 * 
 * @param id - Series ID
 * @param data - Updated series data
 * @returns Updated series
 */
export async function updateSeries(
  id: number,
  data: UpdateSeriesRequest
): Promise<Series> {
  const response = await fetchApi<SeriesResponse>(
    `/api/series/${id}`,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    }
  );
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Failed to update series');
}

/**
 * Delete a series.
 * 
 * @param id - Series ID
 */
export async function deleteSeries(id: number): Promise<void> {
  const response = await fetchApi<ApiResult<{ message: string }>>(
    `/api/series/${id}`,
    { method: 'DELETE' }
  );
  
  if (!response.success) {
    throw new Error((response as { error: string }).error || 'Failed to delete series');
  }
}

/**
 * Get series info for an essay.
 * 
 * @param slug - Essay URL slug
 * @returns Array of series info with navigation
 * 
 * Called by EssayDetailPage for series navigation.
 */
export async function getEssaySeriesInfo(slug: string): Promise<EssaySeriesInfo[]> {
  const response = await fetchApi<EssaySeriesResponse>(
    `/api/essays/${encodeURIComponent(slug)}/series`
  );
  
  if (response.success && response.data) {
    return response.data.seriesInfo;
  }
  
  throw new Error(response.error || 'Failed to fetch essay series info');
}

/**
 * Add or update an essay in a series.
 * 
 * @param slug - Essay URL slug
 * @param seriesId - Series ID
 * @param orderIndex - Position in series
 */
export async function addEssayToSeries(
  slug: string,
  seriesId: number,
  orderIndex: number
): Promise<void> {
  const response = await fetchApi<ApiResult<{ message: string }>>(
    `/api/essays/${encodeURIComponent(slug)}/series`,
    {
      method: 'PUT',
      body: JSON.stringify({ seriesId, orderIndex }),
    }
  );
  
  if (!response.success) {
    throw new Error((response as { error: string }).error || 'Failed to add essay to series');
  }
}

/**
 * Remove an essay from a series.
 * 
 * @param slug - Essay URL slug
 * @param seriesId - Series ID
 */
export async function removeEssayFromSeries(
  slug: string,
  seriesId: number
): Promise<void> {
  const response = await fetchApi<ApiResult<{ message: string }>>(
    `/api/essays/${encodeURIComponent(slug)}/series/${seriesId}`,
    { method: 'DELETE' }
  );
  
  if (!response.success) {
    throw new Error((response as { error: string }).error || 'Failed to remove essay from series');
  }
}

// =============================================================================
// Search API (Phase 4)
// =============================================================================

/**
 * Response type for search endpoint.
 */
interface SearchApiResponse {
  success: boolean;
  data?: SearchResponse;
  error?: string;
}

/**
 * Search essays using full-text search.
 * 
 * @param query - Search query
 * @param options - Optional filters (category, tag, status, limit, offset)
 * @returns Search results with highlighted snippets
 * 
 * Called by SearchBar and SearchResultsPage components.
 */
export async function searchEssays(
  query: string,
  options?: {
    category?: string;
    tag?: string;
    status?: EssayStatus;
    limit?: number;
    offset?: number;
  }
): Promise<SearchResponse> {
  const params = new URLSearchParams();
  params.set('q', query);
  if (options?.category) params.set('category', options.category);
  if (options?.tag) params.set('tag', options.tag);
  if (options?.status) params.set('status', options.status);
  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.offset) params.set('offset', options.offset.toString());

  const response = await fetchApi<SearchApiResponse>(
    `/api/search?${params.toString()}`
  );
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Search failed');
}

/**
 * Semantic search across essays and notes using embeddings.
 * Generates an embedding for the query server-side and compares against all content.
 */
export interface SemanticSearchResult {
  type: 'essay' | 'note';
  id: number;
  slug?: string;
  title: string;
  preview: string;
  notebook?: string;
  status?: string;
  updatedAt: string;
  similarity: number;
}

export interface SemanticSearchResponse {
  results: SemanticSearchResult[];
  query: string;
  total: number;
}

export async function semanticSearch(
  query: string,
  limit = 10
): Promise<SemanticSearchResponse> {
  const params = new URLSearchParams();
  params.set('q', query);
  params.set('limit', limit.toString());

  const response = await fetchApi<{ success: boolean; data: SemanticSearchResponse; error?: string }>(
    `/api/search/semantic?${params.toString()}`
  );

  if (response.success && response.data) {
    return response.data;
  }

  throw new Error(response.error || 'Semantic search failed');
}

// =============================================================================
// Public API (Phase 5) - No authentication required
// =============================================================================

/**
 * Public essay type with all relations for display.
 */
export interface PublicEssay {
  id: number;
  slug: string;
  title: string;
  content: string;
  preview: string;
  readingTime: number;
  wordCount: number;
  publishedAt: string | null;
  author: PublicUser;
  category: Category | null;
  tags: Tag[];
  seriesInfo: EssaySeriesInfo[];
  attachments: Attachment[];
  tableOfContents: Array<{ id: string; text: string; level: number }>;
  trackEmbed: string | null;
  lyrics: string | null;
  hasAudio: boolean;
}

/**
 * Public essay list item (summary without full content).
 */
export interface PublicEssayListItem {
  id: number;
  slug: string;
  title: string;
  preview: string;
  readingTime: number;
  publishedAt: string | null;
  author: PublicUser;
  category: Category | null;
  tags: Tag[];
}

/**
 * Public comment with user and replies.
 */
export interface PublicComment {
  id: number;
  content: string;
  createdAt: string;
  user: PublicUser;
  replies: PublicComment[];
}

/**
 * Category with essay count.
 */
export interface CategoryWithCount extends Category {
  essayCount: number;
}

/**
 * Tag with essay count.
 */
export interface TagWithCount extends Tag {
  essayCount: number;
}

/**
 * Response types for public API endpoints.
 */
interface PublicEssaysResponse {
  success: boolean;
  data?: {
    essays: PublicEssayListItem[];
    total: number;
    limit: number;
    offset: number;
  };
  error?: string;
}

interface PublicEssayResponse {
  success: boolean;
  data?: PublicEssay;
  error?: string;
}

interface PublicCommentsResponse {
  success: boolean;
  data?: { comments: PublicComment[] };
  error?: string;
}

interface PublicCategoriesResponse {
  success: boolean;
  data?: { categories: CategoryWithCount[] };
  error?: string;
}

interface PublicTagsResponse {
  success: boolean;
  data?: { tags: TagWithCount[] };
  error?: string;
}

interface PublicSeriesResponse {
  success: boolean;
  data?: { series: Series[] };
  error?: string;
}

interface PublicSeriesDetailResponse {
  success: boolean;
  data?: SeriesWithEssays;
  error?: string;
}

/**
 * Fetch published essays for the public site.
 * No authentication required.
 * 
 * @param options - Optional filters (limit, offset, category, tag)
 * @returns Paginated list of published essays
 * 
 * Called by public pages to display essay listings.
 */
export async function getPublicEssays(options?: {
  limit?: number;
  offset?: number;
  category?: string;
  tag?: string;
}): Promise<{ essays: PublicEssayListItem[]; total: number; limit: number; offset: number }> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.offset) params.set('offset', options.offset.toString());
  if (options?.category) params.set('category', options.category);
  if (options?.tag) params.set('tag', options.tag);

  const url = params.toString()
    ? `/api/public/essays?${params.toString()}`
    : '/api/public/essays';

  const response = await fetchApi<PublicEssaysResponse>(url);
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Failed to fetch essays');
}

/**
 * Fetch a single published essay by slug.
 * No authentication required.
 * 
 * @param slug - Essay URL slug
 * @returns Full essay with content and relations
 * 
 * Called by public essay detail page.
 */
export async function getPublicEssay(slug: string): Promise<PublicEssay> {
  const response = await fetchApi<PublicEssayResponse>(
    `/api/public/essays/${encodeURIComponent(slug)}`
  );
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Essay not found');
}

/**
 * Fetch comments for a published essay.
 * No authentication required.
 * 
 * @param slug - Essay URL slug
 * @returns Nested comment tree
 * 
 * Called by public essay detail page.
 */
export async function getPublicComments(slug: string): Promise<PublicComment[]> {
  const response = await fetchApi<PublicCommentsResponse>(
    `/api/public/essays/${encodeURIComponent(slug)}/comments`
  );
  
  if (response.success && response.data) {
    return response.data.comments;
  }
  
  throw new Error(response.error || 'Failed to fetch comments');
}

/**
 * Post a comment on a published essay.
 * Requires authentication (for public commenters).
 * 
 * @param slug - Essay URL slug
 * @param content - Comment text
 * @param parentId - Optional parent comment ID for replies
 * @returns Created comment
 * 
 * Called by public comment form.
 */
export async function postPublicComment(
  slug: string,
  content: string,
  parentId?: number
): Promise<PublicComment> {
  const response = await fetchApi<{ success: boolean; data?: PublicComment; error?: string }>(
    `/api/public/essays/${encodeURIComponent(slug)}/comments`,
    {
      method: 'POST',
      body: JSON.stringify({ content, parentId }),
    }
  );
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.error || 'Failed to post comment');
}

/**
 * Fetch all categories with essay counts.
 * No authentication required.
 * 
 * @returns Array of categories with counts
 * 
 * Called by public category pages and sidebar.
 */
export async function getPublicCategories(): Promise<CategoryWithCount[]> {
  const response = await fetchApi<PublicCategoriesResponse>('/api/public/categories');
  
  // API returns data directly as an array of categories
  if (response.success && response.data) {
    return response.data as unknown as CategoryWithCount[];
  }
  
  throw new Error(response.error || 'Failed to fetch categories');
}

/**
 * Fetch all tags with essay counts.
 * No authentication required.
 * 
 * @returns Array of tags with counts
 * 
 * Called by public tag pages and sidebar.
 */
export async function getPublicTags(): Promise<TagWithCount[]> {
  const response = await fetchApi<PublicTagsResponse>('/api/public/tags');
  
  // API returns data directly as an array of tags
  if (response.success && response.data) {
    return response.data as unknown as TagWithCount[];
  }
  
  throw new Error(response.error || 'Failed to fetch tags');
}

/**
 * Fetch all series for public display.
 * No authentication required.
 * 
 * @returns Array of series
 * 
 * Called by public series listing.
 */
export async function getPublicSeries(): Promise<Series[]> {
  const response = await fetchApi<PublicSeriesResponse>('/api/public/series');
  
  if (response.success && response.data) {
    return response.data.series;
  }
  
  throw new Error(response.error || 'Failed to fetch series');
}

/**
 * Fetch a series with its published essays.
 * No authentication required.
 * 
 * @param slug - Series URL slug
 * @returns Series with ordered essays
 * 
 * Called by public series detail page.
 */
export async function getPublicSeriesDetail(slug: string): Promise<SeriesWithEssays> {
  const response = await fetchApi<PublicSeriesDetailResponse>(
    `/api/public/series/${encodeURIComponent(slug)}`
  );

  if (response.success && response.data) {
    return response.data;
  }

  throw new Error(response.error || 'Series not found');
}

// =============================================================================
// Audio Generation API (Phase 5)
// =============================================================================

/**
 * Trigger server-side TTS audio generation for an essay.
 * Calls Kokoro sequentially for each segment, writes WAV files and manifest.json.
 * This is a long-running operation — the button should show a loading state.
 *
 * @param slug - Essay URL slug
 * @returns Result with segment count and list of generated filenames
 *
 * Called by EssayDetailPage Generate Audio button.
 */
export async function generateAudio(slug: string): Promise<AudioGenerationResult> {
  const response = await fetchApi<ApiResult<AudioGenerationResult>>(
    `/api/admin/essays/${encodeURIComponent(slug)}/generate-audio`,
    { method: 'POST' }
  );

  if (response.success && response.data) {
    return response.data;
  }

  throw new Error((response as { error?: string }).error || 'Audio generation failed');
}

// =============================================================================
// Notebooks API (Author-only)
// =============================================================================

export async function getNotebooks(): Promise<Notebook[]> {
  const response = await fetchApi<ApiResult<{ notebooks: Notebook[] }>>('/api/notebooks');
  if (response.success && response.data) {
    return response.data.notebooks;
  }
  throw new Error((response as { error?: string }).error || 'Failed to fetch notebooks');
}

export async function createNotebook(data: CreateNotebookRequest): Promise<Notebook> {
  const response = await fetchApi<ApiResult<Notebook>>('/api/notebooks', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (response.success && response.data) {
    return response.data;
  }
  throw new Error((response as { error?: string }).error || 'Failed to create notebook');
}

export async function updateNotebook(id: number, data: UpdateNotebookRequest): Promise<Notebook> {
  const response = await fetchApi<ApiResult<Notebook>>(`/api/notebooks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  if (response.success && response.data) {
    return response.data;
  }
  throw new Error((response as { error?: string }).error || 'Failed to update notebook');
}

export async function deleteNotebook(id: number): Promise<void> {
  const response = await fetchApi<ApiResult<{ message: string }>>(`/api/notebooks/${id}`, {
    method: 'DELETE',
  });
  if (!response.success) {
    throw new Error((response as { error?: string }).error || 'Failed to delete notebook');
  }
}

// =============================================================================
// Notes API (Author-only)
// =============================================================================

export async function getNotes(notebookId?: number, archived?: boolean): Promise<Note[]> {
  const params = new URLSearchParams();
  if (notebookId != null) params.set('notebookId', String(notebookId));
  if (archived != null) params.set('archived', String(archived));
  const qs = params.toString();
  const response = await fetchApi<ApiResult<{ notes: Note[] }>>(`/api/notes${qs ? `?${qs}` : ''}`);
  if (response.success && response.data) {
    return response.data.notes;
  }
  throw new Error((response as { error?: string }).error || 'Failed to fetch notes');
}

export async function createNote(data: CreateNoteRequest): Promise<NoteWithLinks> {
  const response = await fetchApi<ApiResult<NoteWithLinks>>('/api/notes', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (response.success && response.data) {
    return response.data;
  }
  throw new Error((response as { error?: string }).error || 'Failed to create note');
}

export async function getNote(id: number): Promise<NoteWithLinks> {
  const response = await fetchApi<ApiResult<NoteWithLinks>>(`/api/notes/${id}`);
  if (response.success && response.data) {
    return response.data;
  }
  throw new Error((response as { error?: string }).error || 'Failed to fetch note');
}

export async function updateNote(id: number, data: UpdateNoteRequest): Promise<NoteWithLinks> {
  const response = await fetchApi<ApiResult<NoteWithLinks>>(`/api/notes/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  if (response.success && response.data) {
    return response.data;
  }
  throw new Error((response as { error?: string }).error || 'Failed to update note');
}

export async function deleteNote(id: number): Promise<void> {
  const response = await fetchApi<ApiResult<{ message: string }>>(`/api/notes/${id}`, {
    method: 'DELETE',
  });
  if (!response.success) {
    throw new Error((response as { error?: string }).error || 'Failed to delete note');
  }
}

export async function archiveNote(id: number): Promise<Note> {
  const response = await fetchApi<ApiResult<Note>>(`/api/notes/${id}/archive`, {
    method: 'PUT',
  });
  if (response.success && response.data) {
    return response.data;
  }
  throw new Error((response as { error?: string }).error || 'Failed to archive note');
}

export async function unarchiveNote(id: number): Promise<Note> {
  const response = await fetchApi<ApiResult<Note>>(`/api/notes/${id}/unarchive`, {
    method: 'PUT',
  });
  if (response.success && response.data) {
    return response.data;
  }
  throw new Error((response as { error?: string }).error || 'Failed to unarchive note');
}

// =============================================================================
// Note Search API (Author-only)
// =============================================================================

export async function searchNotes(query: string, limit = 20): Promise<NoteSearchResponse> {
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  const response = await fetchApi<ApiResult<NoteSearchResponse>>(`/api/notes/search?${params}`);
  if (response.success && response.data) {
    return response.data;
  }
  throw new Error((response as { error?: string }).error || 'Note search failed');
}

// =============================================================================
// Note-Essay Link Management (Author-only)
// =============================================================================

export async function addNoteLink(noteId: number, essayId: number): Promise<void> {
  const response = await fetchApi<ApiResult<unknown>>(`/api/notes/${noteId}/links`, {
    method: 'POST',
    body: JSON.stringify({ essayId }),
  });
  if (!response.success) {
    throw new Error((response as { error?: string }).error || 'Failed to link essay');
  }
}

export async function removeNoteLink(noteId: number, essayId: number): Promise<void> {
  const response = await fetchApi<ApiResult<unknown>>(`/api/notes/${noteId}/links/${essayId}`, {
    method: 'DELETE',
  });
  if (!response.success) {
    throw new Error((response as { error?: string }).error || 'Failed to unlink essay');
  }
}

export async function reprocessNote(id: number): Promise<void> {
  const response = await fetchApi<ApiResult<unknown>>(`/api/notes/${id}/reprocess`, {
    method: 'POST',
  });
  if (!response.success) {
    throw new Error((response as { error?: string }).error || 'Failed to reprocess note');
  }
}

export async function acceptNoteTags(noteId: number, tags: string[]): Promise<Note> {
  const response = await fetchApi<ApiResult<Note>>(`/api/notes/${noteId}/accept-tags`, {
    method: 'POST',
    body: JSON.stringify({ tags }),
  });
  if (!response.success || !response.data) {
    throw new Error((response as { error?: string }).error || 'Failed to accept tags');
  }
  return response.data;
}

export async function getSimilarNotes(noteId: number): Promise<SimilarItemsResponse> {
  const response = await fetchApi<ApiResult<SimilarItemsResponse>>(`/api/notes/${noteId}/similar`);
  if (!response.success || !response.data) {
    throw new Error((response as { error?: string }).error || 'Failed to get similar items');
  }
  return response.data;
}
